import { useState, useEffect, useCallback } from 'react';
import { getForms, getStaff, getContractors } from '../utils/api';
import api from '../utils/api';
import Attachments from './Attachments';
import JobCalculator from '../pages/JobCalculator';
import toast from 'react-hot-toast';

const SERVICES = ['Roof', 'EC', 'Win', 'Gut'];
const SERVICE_LABELS = { Roof: 'Roofing', EC: 'Exterior Coating', Win: 'Windows', Gut: 'Gutters' };
const CASH_TYPES = ['Deposit', 'Rem Collect', 'Start Collect', 'Complete Collect'];
const LENDER_SLOTS = [1, 2, 3];

const emptyJob = {
  po_number: '', customer_name: '', customer_name_2: '', address: '', city: '', phone: '', phone_2: '', email: '',
  contract_date: '', contact_date: '', contract_amt: '', change_order_amt: '',
  sales_status: 'Active', prod_status: '',
  rem_date: '', start_date: '', end_date: '',
  cert_ready: '', cust_signed: '', funded: '',
  sales_rep: '', sales_rep_2: '', sales_rep_split: '100', sales_rep_2_split: '0',
  telemarketer: '', telemarketer_2: '', telemarketer_split: '100', telemarketer_2_split: '0',
  canvasser: '', canvasser_2: '', canvasser_split: '100', canvasser_2_split: '0',
  confirmer: '',
  con_roof: '', con_ec: '', con_win: '', con_gut: '',
  contractor_invoice_amt: '',
  sale_price: '',
  bid_sent: '', bid_rec: '', bid_pct: '', welcome: '',
  color: '', scope: '', permit: '', materials: '',
  svc1: '', svc2: '', svc3: '', svc4: '',
  calc_sell_price: '', calc_job_cost: '', calc_profit: '',
  notes: '',
};

function emptyLender(service, slot) {
  return { service, lender_slot: slot, lender_name: '', amount: '', approved_date: '', status: '', funded_date: '' };
}
function emptyCash(service, type) {
  return { service, payment_type: type, amount: '', payment_date: '', pay_method: '', check_num: '', status: '' };
}
function buildDefaultSubs() {
  const ldrs = [], cash = [];
  for (const svc of SERVICES) {
    for (const slot of LENDER_SLOTS) ldrs.push(emptyLender(svc, slot));
    for (const type of CASH_TYPES) cash.push(emptyCash(svc, type));
  }
  return { ldrs, cash };
}

function TextField({ label, field, type, placeholder, value, onChange }) {
  return (
    <div className="form-group">
      <label className="form-label">{label}</label>
      <input className="form-input" type={type||'text'} placeholder={placeholder||''}
        value={value||''} onChange={e => onChange(field, e.target.value)} />
    </div>
  );
}

function SelectField({ label, field, options, allowBlank, value, onChange }) {
  return (
    <div className="form-group">
      <label className="form-label">{label}</label>
      <select className="form-select" value={value||''} onChange={e => onChange(field, e.target.value)}>
        {allowBlank !== false && <option value="">—</option>}
        {options.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}

function StaffSplitField({ label, options, field1, field2, split1, split2, value1, value2, splitVal1, splitVal2, onChange }) {
  const hasSecond = !!(value2);
  const [showSecond, setShowSecond] = useState(hasSecond);

  function handleToggle() {
    if (showSecond) {
      onChange(field2, '');
      onChange(split1, '100');
      onChange(split2, '0');
      setShowSecond(false);
    } else {
      onChange(split1, '50');
      onChange(split2, '50');
      setShowSecond(true);
    }
  }

  function handleSplit1(val) {
    const v = Math.min(100, Math.max(0, parseInt(val) || 0));
    onChange(split1, String(v));
    onChange(split2, String(100 - v));
  }

  const total = parseInt(splitVal1 || 0) + parseInt(splitVal2 || 0);
  const splitOk = !showSecond || total === 100;

  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
        <label style={{ fontSize: 12, fontWeight: 600, color: '#555', textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</label>
        <button type="button" onClick={handleToggle}
          style={{ fontSize: 11, padding: '2px 8px', borderRadius: 4, border: '1px solid #ddd',
            background: showSecond ? '#e8edf5' : '#fff', color: showSecond ? '#1a355f' : '#888', cursor: 'pointer' }}>
          {showSecond ? '− Remove 2nd' : '+ Add 2nd'}
        </button>
      </div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <select className="form-select" style={{ flex: 1 }} value={value1 || ''} onChange={e => onChange(field1, e.target.value)}>
          <option value="">— Select —</option>
          {options.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
        {showSecond && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
            <input type="number" className="form-input" style={{ width: 56, textAlign: 'center', padding: '6px 4px' }}
              min="0" max="100" value={splitVal1 || '100'} onChange={e => handleSplit1(e.target.value)} />
            <span style={{ fontSize: 12, color: '#888' }}>%</span>
          </div>
        )}
      </div>
      {showSecond && (
        <>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 6 }}>
            <select className="form-select" style={{ flex: 1 }} value={value2 || ''} onChange={e => onChange(field2, e.target.value)}>
              <option value="">— Select 2nd —</option>
              {options.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
              <input type="number" className="form-input" style={{ width: 56, textAlign: 'center', padding: '6px 4px', background: '#f5f5f5' }}
                value={splitVal2 || '0'} readOnly />
              <span style={{ fontSize: 12, color: '#888' }}>%</span>
            </div>
          </div>
          <div style={{ fontSize: 11, marginTop: 4, color: splitOk ? '#1a355f' : '#c62828' }}>
            Split total: {total}% {splitOk ? '✓' : '— must equal 100%'}
          </div>
        </>
      )}
    </div>
  );
}

export default function JobForm({ initial, onSubmit, submitLabel='Save', loading=false, jobId=null, onAutoSave=null }) {
  const [job, setJob] = useState({ ...emptyJob, ...initial });
  const [lenders, setLenders] = useState([]);
  const [cashPayments, setCashPayments] = useState([]);
  const [forms, setForms] = useState({});
  const [staff, setStaff] = useState([]);
  const [contractors, setContractors] = useState([]);
  const [activeTab, setActiveTab] = useState(() => {
    try {
      const t = new URLSearchParams(window.location.search).get('tab');
      const allowed = ['core','services','lenders','cash','production','calculator','attachments','contracts'];
      return allowed.includes(t) ? t : 'core';
    } catch { return 'core'; }
  });
  const [autoSavedId, setAutoSavedId] = useState(jobId);
  const [contractLinks, setContractLinks] = useState({}); // type -> full url

  async function makeLink(type) {
    try {
      const res = await api.post(`/contracts/link/${type}/${autoSavedId}`, { prefill: {
        customer_name: job.customer_name || '', address: job.address || '',
        buyer_name: job.customer_name || '', property_address: job.address || '',
        auth_number: job.po_number || '',
      } });
      const full = `${window.location.origin}${res.data.path}`;
      setContractLinks(s => ({ ...s, [type]: full }));
      try { await navigator.clipboard.writeText(full); toast.success('Link copied — paste it to your customer.'); }
      catch { toast.success('Link created.'); }
    } catch (e) {
      toast.error('Could not create link. ' + (e?.response?.data?.error || ''));
    }
  }
  const [autoSaving, setAutoSaving] = useState(false);

  async function handleTabChange(tabId) {
    if ((tabId === 'attachments' || tabId === 'contracts') && !autoSavedId && onAutoSave) {
      if (!job.po_number?.trim() || !job.customer_name?.trim()) {
        toast.error('Please enter PO# and Customer Name before adding attachments');
        return;
      }
      setAutoSaving(true);
      try {
        const saved = await onAutoSave({ ...job, lender_payments: [], cash_payments: [] });
        setAutoSavedId(saved.id);
        toast.success('Record auto-saved — you can now add attachments');
      } catch (err) {
        toast.error(err.response?.data?.error || 'Could not save record');
        setAutoSaving(false);
        return;
      }
      setAutoSaving(false);
    }
    setActiveTab(tabId);
  }

  useEffect(() => {
    Promise.all([getForms(), getStaff(), getContractors()]).then(([f, s, c]) => {
      setForms(f); setStaff(s); setContractors(c);
    });
    const { ldrs, cash } = buildDefaultSubs();
    if (initial?.lender_payments?.length) {
      setLenders(ldrs.map(e => {
        const found = initial.lender_payments.find(lp => lp.service===e.service && lp.lender_slot===e.lender_slot);
        return found ? { ...e, ...found } : e;
      }));
    } else { setLenders(ldrs); }
    if (initial?.cash_payments?.length) {
      setCashPayments(cash.map(e => {
        const found = initial.cash_payments.find(cp => cp.service===e.service && cp.payment_type===e.payment_type);
        return found ? { ...e, ...found } : e;
      }));
    } else { setCashPayments(cash); }
  }, []);

  const setJobField = useCallback((field, value) => {
    setJob(j => ({ ...j, [field]: value }));
  }, []);

  function setLender(service, slot, field, value) {
    setLenders(prev => prev.map(l => l.service===service && l.lender_slot===slot ? {...l,[field]:value} : l));
  }
  function setCash(service, type, field, value) {
    setCashPayments(prev => prev.map(c => c.service===service && c.payment_type===type ? {...c,[field]:value} : c));
  }
  function getLender(service, slot) {
    return lenders.find(l => l.service===service && l.lender_slot===slot) || emptyLender(service, slot);
  }
  function getCash(service, type) {
    return cashPayments.find(c => c.service===service && c.payment_type===type) || emptyCash(service, type);
  }

  function handleSubmit(e) {
    e.preventDefault();
    if (!job.po_number?.trim()) return toast.error('PO# is required');
    if (!job.customer_name?.trim()) return toast.error('Customer name is required');
    // Validate splits
    if (job.sales_rep_2 && parseInt(job.sales_rep_split||0) + parseInt(job.sales_rep_2_split||0) !== 100) {
      return toast.error('Sales Rep split must equal 100%');
    }
    if (job.telemarketer_2 && parseInt(job.telemarketer_split||0) + parseInt(job.telemarketer_2_split||0) !== 100) {
      return toast.error('Telemarketer split must equal 100%');
    }
    if (job.canvasser_2 && parseInt(job.canvasser_split||0) + parseInt(job.canvasser_2_split||0) !== 100) {
      return toast.error('Canvasser split must equal 100%');
    }
    onSubmit({ ...job, lender_payments: lenders.filter(l => l.lender_name?.trim()), cash_payments: cashPayments.filter(c => c.amount||c.status) });
  }

  const dropOpts = cat => (forms[cat]||[]).map(f => f.value);
  const serviceTypes = dropOpts('service_types').length
    ? dropOpts('service_types')
    : ['Roofing','Exterior Coating','Windows','Gutters','Patio Cover','Fencing','Bathroom','Kitchen','Cement','Other'];
  const activeStaff = staff.filter(s => s.status==='Active');
  const salesReps = activeStaff.filter(s => s.role==='Sales Rep').map(s => s.name);
  const telemarketers = activeStaff.filter(s => s.role==='Telemarketer').map(s => s.name);
  const canvassers = activeStaff.filter(s => s.role==='Canvasser').map(s => s.name);
  const confirmers = activeStaff.filter(s => s.role==='Confirmer').map(s => s.name);
  const conNames = contractors.filter(c => c.status==='Active').map(c => c.company_name);
  const tabs = [{id:'core',label:'Core Info'},{id:'services',label:'Services & Contractors'},{id:'lenders',label:'Lender Financing'},{id:'cash',label:'Cash Payments'},{id:'production',label:'Production'},{id:'calculator',label:'Calculator 🧮'},{id:'attachments',label:'Attachments 📎'},{id:'contracts',label:'Contracts 🖊️'}];

  // Bid vs. Actual (shown at the top of the record, updates live as the invoice is typed)
  const bvaPackets = Array.isArray(job.contractor_packets) ? job.contractor_packets : [];
  const bvaBids = bvaPackets.filter(p => Number(p.total_bid) > 0);
  const bvaInvoice = Number(job.contractor_invoice_amt) || 0;
  const bvaTotalBid = bvaBids.reduce((s, p) => s + (Number(p.total_bid) || 0), 0);
  const bvaShow = bvaBids.length > 0 || bvaInvoice > 0 || !!jobId;
  const bvaEmpty = bvaTotalBid === 0 && bvaInvoice === 0;
  const bvaFmt = n => '$' + (Number(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const bvaDiff = bvaInvoice - bvaTotalBid;
  const bvaOver = bvaTotalBid > 0 && bvaInvoice > bvaTotalBid;
  const bvaMax = Math.max(bvaTotalBid, bvaInvoice, 1);

  // Per-record P&L: manual Sale Price minus job cost (from the calculator)
  const pnlSold = Number(job.sale_price) || 0;
  const pnlCost = Number(job.calc_job_cost) || 0;
  const pnlProfit = pnlSold - pnlCost;
  const pnlMargin = pnlSold > 0 ? (pnlProfit / pnlSold * 100) : 0;
  const pnlShow = pnlSold > 0 || pnlCost > 0 || !!jobId;

  return (
    <form onSubmit={handleSubmit}>
      {pnlShow && (
        <div style={{ marginBottom: 16, padding: '14px 16px', background: '#fff', border: '1px solid #e3e3e3', borderRadius: 10 }}>
          <div style={{ fontWeight: 700, color: '#0d1f3b', fontSize: 13, marginBottom: 10 }}>Profit &amp; Loss (this job)</div>
          <div style={{ maxWidth: 380 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '5px 0', fontSize: 13 }}>
              <span style={{ color: '#555' }}>Sale price</span>
              <input type="number" step="0.01" value={job.sale_price ?? ''}
                onChange={e => setJobField('sale_price', e.target.value)}
                placeholder="0.00"
                style={{ width: 140, textAlign: 'right', padding: '4px 8px', border: '1px solid #ccc', borderRadius: 6, fontSize: 13, fontWeight: 600 }} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', fontSize: 13, borderBottom: '1px solid #eee' }}>
              <span style={{ color: '#555' }}>Job cost (calculator)</span>
              <span style={{ fontWeight: 600, color: '#854F0B' }}>{pnlCost > 0 ? '−' + bvaFmt(pnlCost) : '—'}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0 0', fontSize: 14, fontWeight: 700 }}>
              <span>Profit</span>
              <span style={{ color: pnlProfit >= 0 ? '#1a355f' : '#c62828' }}>
                {bvaFmt(pnlProfit)}{pnlSold > 0 && pnlCost > 0 ? `  (${pnlMargin.toFixed(1)}%)` : ''}
              </span>
            </div>
          </div>
          {pnlCost === 0 && (
            <div style={{ marginTop: 8, fontSize: 11, color: '#888' }}>
              Save a cost from the Calculator tab to see job cost and profit.
            </div>
          )}
        </div>
      )}
      {bvaShow && (
        <div style={{ marginBottom: 16, padding: '12px 16px', background: '#fff', border: '1px solid #e3e3e3', borderRadius: 10 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, flexWrap: 'wrap', gap: 6 }}>
            <span style={{ fontWeight: 700, color: '#0d1f3b', fontSize: 13 }}>Bid vs. Actual</span>
            {bvaTotalBid > 0 && bvaInvoice > 0 && (
              <span style={{ fontSize: 12, fontWeight: 700, color: bvaOver ? '#c62828' : '#1a355f' }}>
                {bvaOver ? `Over bid by ${bvaFmt(bvaDiff)}` : bvaDiff === 0 ? 'Invoice matches bid' : `Under bid by ${bvaFmt(Math.abs(bvaDiff))}`}
              </span>
            )}
          </div>
          {bvaEmpty ? (
            <div style={{ fontSize: 12, color: '#888' }}>
              No contractor bid or invoice recorded yet. The contractor's packet bid appears here automatically; enter the Contractor Invoice Amount in the Services &amp; Contractors tab to compare.
            </div>
          ) : (
          <>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ width: 52, fontSize: 11, color: '#888' }}>Bid</div>
              <div style={{ flex: 1, background: '#f0f0f0', borderRadius: 4, height: 16 }}>
                <div style={{ width: `${(bvaTotalBid / bvaMax) * 100}%`, background: '#1a355f', height: '100%', borderRadius: 4 }} />
              </div>
              <div style={{ width: 120, textAlign: 'right', fontSize: 12, fontWeight: 600 }}>{bvaFmt(bvaTotalBid)}</div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ width: 52, fontSize: 11, color: '#888' }}>Actual</div>
              <div style={{ flex: 1, background: '#f0f0f0', borderRadius: 4, height: 16 }}>
                <div style={{ width: `${(bvaInvoice / bvaMax) * 100}%`, background: '#C8A24B', height: '100%', borderRadius: 4 }} />
              </div>
              <div style={{ width: 120, textAlign: 'right', fontSize: 12, fontWeight: 600 }}>{bvaFmt(bvaInvoice)}</div>
            </div>
          </div>
          {bvaBids.length > 0 && (
            <div style={{ marginTop: 8, fontSize: 11, color: '#888' }}>
              Bid from: {bvaBids.map(p => `${p.contractor_name}${p.submission_number ? ` (#${p.submission_number})` : ''} ${bvaFmt(p.total_bid)}`).join('  ·  ')}
            </div>
          )}
          {bvaInvoice === 0 && bvaTotalBid > 0 && (
            <div style={{ marginTop: 8, fontSize: 11, color: '#888' }}>
              Enter the Contractor Invoice Amount in the Services &amp; Contractors tab to compare.
            </div>
          )}
          </>
          )}
        </div>
      )}
      <div className="tabs">
        {tabs.map(t => (
          <button type="button" key={t.id} className={`tab${activeTab===t.id?' active':''}`} onClick={() => handleTabChange(t.id)} disabled={autoSaving}>{autoSaving && t.id==='attachments' ? 'Saving…' : t.label}</button>
        ))}
      </div>

      {activeTab==='core' && (
        <>
          <div className="form-section">
            <div className="form-section-title">Job Identity</div>
            <div className="form-grid form-grid-3">
              <TextField label="PO Number *" field="po_number" placeholder="26-XXXX-X" value={job.po_number} onChange={setJobField} />
              <TextField label="Customer Name *" field="customer_name" value={job.customer_name} onChange={setJobField} />
              <TextField label="Customer Name 2" field="customer_name_2" value={job.customer_name_2} onChange={setJobField} />
              <TextField label="Phone" field="phone" type="tel" value={job.phone} onChange={setJobField} />
              <TextField label="Phone 2" field="phone_2" type="tel" value={job.phone_2} onChange={setJobField} />
              <TextField label="Email" field="email" type="email" value={job.email} onChange={setJobField} />
              <TextField label="Address" field="address" value={job.address} onChange={setJobField} />
              <TextField label="City" field="city" value={job.city} onChange={setJobField} />
            </div>
          </div>
          <div className="form-section">
            <div className="form-section-title">Contract</div>
            <div className="form-grid form-grid-3">
              <TextField label="Contract Date" field="contract_date" type="date" value={job.contract_date} onChange={setJobField} />
              <TextField label="Contract Amount" field="contract_amt" type="number" value={job.contract_amt} onChange={setJobField} />
              <TextField label="Change Order Amount" field="change_order_amt" type="number" value={job.change_order_amt} onChange={setJobField} />
              <SelectField label="Sales Status" field="sales_status" options={dropOpts('sales_status')} allowBlank={false} value={job.sales_status} onChange={setJobField} />
              <SelectField label="Production Status" field="prod_status" options={dropOpts('prod_status')} value={job.prod_status} onChange={setJobField} />
            </div>
          </div>

          <div className="form-section">
            <div className="form-section-title">Team</div>
            <StaffSplitField
              label="Sales Rep"
              options={salesReps.length ? salesReps : staff.map(s => s.name)}
              field1="sales_rep" field2="sales_rep_2"
              split1="sales_rep_split" split2="sales_rep_2_split"
              value1={job.sales_rep} value2={job.sales_rep_2}
              splitVal1={job.sales_rep_split} splitVal2={job.sales_rep_2_split}
              onChange={setJobField}
            />
            <StaffSplitField
              label="Telemarketer"
              options={telemarketers}
              field1="telemarketer" field2="telemarketer_2"
              split1="telemarketer_split" split2="telemarketer_2_split"
              value1={job.telemarketer} value2={job.telemarketer_2}
              splitVal1={job.telemarketer_split} splitVal2={job.telemarketer_2_split}
              onChange={setJobField}
            />
            <SelectField label="Confirmer" field="confirmer" options={confirmers} value={job.confirmer} onChange={setJobField} />

            <StaffSplitField
              label="Canvasser"
              options={canvassers}
              field1="canvasser" field2="canvasser_2"
              split1="canvasser_split" split2="canvasser_2_split"
              value1={job.canvasser} value2={job.canvasser_2}
              splitVal1={job.canvasser_split} splitVal2={job.canvasser_2_split}
              onChange={setJobField}
            />
          </div>

          <div className="form-section">
            <div className="form-section-title">Additional</div>
            <div className="form-grid form-grid-3">
              <TextField label="Contact Date" field="contact_date" type="date" value={job.contact_date} onChange={setJobField} />
              <TextField label="Welcome" field="welcome" value={job.welcome} onChange={setJobField} />
              <TextField label="Color" field="color" value={job.color} onChange={setJobField} />
              <TextField label="Bid Sent" field="bid_sent" type="date" value={job.bid_sent} onChange={setJobField} />
              <TextField label="Bid Received" field="bid_rec" type="date" value={job.bid_rec} onChange={setJobField} />
              <TextField label="Bid %" field="bid_pct" value={job.bid_pct} onChange={setJobField} />
            </div>
            <div style={{marginTop:12}}>
              <div className="form-group">
                <label className="form-label">Notes</label>
                <textarea className="form-textarea" value={job.notes||''} onChange={e => setJobField('notes', e.target.value)} rows={3} />
              </div>
            </div>
          </div>
        </>
      )}

      {activeTab==='services' && (
        <div className="form-section">
          <div className="form-section-title">Service Types & Assigned Contractors</div>
          {[{svc:'svc1',con:'con_roof',label:'Service 1 (Roof)'},{svc:'svc2',con:'con_ec',label:'Service 2 (EC)'},{svc:'svc3',con:'con_win',label:'Service 3 (Win)'},{svc:'svc4',con:'con_gut',label:'Service 4 (Gut)'}].map(({svc,con,label}) => (
            <div key={svc} className="service-block" style={{marginBottom:12}}>
              <div className="service-block-header">{label}</div>
              <div className="service-block-body">
                <div className="form-grid form-grid-2">
                  <SelectField label="Service Type" field={svc} options={serviceTypes} value={job[svc]} onChange={setJobField} />
                  <SelectField label="Contractor" field={con} options={conNames} value={job[con]} onChange={setJobField} />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {activeTab==='services' && (
        <div className="form-section">
          <div className="form-section-title">Contractor Billing</div>
          <div className="form-grid form-grid-3">
            <TextField label="Contractor Invoice Amount" field="contractor_invoice_amt" type="number" value={job.contractor_invoice_amt} onChange={setJobField} />
          </div>
          <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
            The actual amount invoiced by the contractor for this job. The Bid vs. Actual comparison appears at the top of the record.
          </div>
        </div>
      )}

      {activeTab==='lenders' && (
        <>
          {SERVICES.map(svc => (
            <div key={svc} className="form-section">
              <div className="form-section-title">{SERVICE_LABELS[svc]} Lenders</div>
              {LENDER_SLOTS.map(slot => {
                const ldr = getLender(svc, slot);
                return (
                  <div key={slot} className="service-block" style={{marginBottom:10}}>
                    <div className="service-block-header">Lender {slot}</div>
                    <div className="service-block-body">
                      <div className="form-grid form-grid-3">
                        <div className="form-group">
                          <label className="form-label">Lender Name</label>
                          <select className="form-select" value={ldr.lender_name||''} onChange={e => setLender(svc,slot,'lender_name',e.target.value)}>
                            <option value="">—</option>
                            {dropOpts('loan_providers').map(o => <option key={o} value={o}>{o}</option>)}
                          </select>
                        </div>
                        <div className="form-group">
                          <label className="form-label">Amount</label>
                          <input className="form-input" type="number" value={ldr.amount||''} onChange={e => setLender(svc,slot,'amount',e.target.value)} />
                        </div>
                        <div className="form-group">
                          <label className="form-label">Approved Date</label>
                          <input className="form-input" type="date" value={ldr.approved_date||''} onChange={e => setLender(svc,slot,'approved_date',e.target.value)} />
                        </div>
                        <div className="form-group">
                          <label className="form-label">Status</label>
                          <select className="form-select" value={ldr.status||''} onChange={e => setLender(svc,slot,'status',e.target.value)}>
                            <option value="">—</option>
                            {['Pending','Approved','Funded','No Go','Cancelled'].map(o => <option key={o} value={o}>{o}</option>)}
                          </select>
                        </div>
                        <div className="form-group">
                          <label className="form-label">Funded Date</label>
                          <input className="form-input" type="date" value={ldr.funded_date||''} onChange={e => setLender(svc,slot,'funded_date',e.target.value)} />
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </>
      )}

      {activeTab==='cash' && (
        <>
          {SERVICES.map(svc => (
            <div key={svc} className="form-section">
              <div className="form-section-title">{SERVICE_LABELS[svc]} Cash Payments</div>
              <div style={{display:'grid',gap:10}}>
                {CASH_TYPES.map(type => {
                  const cp = getCash(svc, type);
                  return (
                    <div key={type} className="service-block">
                      <div className="service-block-header">{type}</div>
                      <div className="service-block-body">
                        <div className="form-grid form-grid-3">
                          <div className="form-group">
                            <label className="form-label">Amount</label>
                            <input className="form-input" type="number" value={cp.amount||''} onChange={e => setCash(svc,type,'amount',e.target.value)} />
                          </div>
                          <div className="form-group">
                            <label className="form-label">Date</label>
                            <input className="form-input" type="date" value={cp.payment_date||''} onChange={e => setCash(svc,type,'payment_date',e.target.value)} />
                          </div>
                          <div className="form-group">
                            <label className="form-label">Pay Method</label>
                            <select className="form-select" value={cp.pay_method||''} onChange={e => setCash(svc,type,'pay_method',e.target.value)}>
                              <option value="">—</option>
                              {(dropOpts('pay_methods').length?dropOpts('pay_methods'):['Cash','Check','Credit Card','ACH','Zelle']).map(o => <option key={o} value={o}>{o}</option>)}
                            </select>
                          </div>
                          <div className="form-group">
                            <label className="form-label">Check #</label>
                            <input className="form-input" type="text" value={cp.check_num||''} onChange={e => setCash(svc,type,'check_num',e.target.value)} />
                          </div>
                          <div className="form-group">
                            <label className="form-label">Status</label>
                            <select className="form-select" value={cp.status||''} onChange={e => setCash(svc,type,'status',e.target.value)}>
                              <option value="">—</option>
                              {['Due','Collected','Waived','N/A'].map(o => <option key={o} value={o}>{o}</option>)}
                            </select>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </>
      )}

      {activeTab==='production' && (
        <>
          <div className="form-section">
            <div className="form-section-title">Scheduling</div>
            <div className="form-grid form-grid-3">
              <TextField label="Removal Date" field="rem_date" type="date" value={job.rem_date} onChange={setJobField} />
              <TextField label="Start Date" field="start_date" type="date" value={job.start_date} onChange={setJobField} />
              <TextField label="End Date" field="end_date" type="date" value={job.end_date} onChange={setJobField} />
            </div>
          </div>
          <div className="form-section">
            <div className="form-section-title">Milestones</div>
            <div className="form-grid form-grid-3">
              <TextField label="Cert Ready" field="cert_ready" value={job.cert_ready} onChange={setJobField} />
              <TextField label="Customer Signed" field="cust_signed" value={job.cust_signed} onChange={setJobField} />
              <TextField label="Funded" field="funded" value={job.funded} onChange={setJobField} />
            </div>
          </div>
          <div className="form-section">
            <div className="form-section-title">Job Details</div>
            <div className="form-grid form-grid-3">
              <SelectField label="Permit Status" field="permit" options={dropOpts('permit_status')} value={job.permit} onChange={setJobField} />
              <SelectField label="Materials Status" field="materials" options={dropOpts('material_status')} value={job.materials} onChange={setJobField} />
              <TextField label="Scope" field="scope" value={job.scope} onChange={setJobField} />
            </div>
          </div>
        </>
      )}

      {activeTab==='calculator' && (
        <div className="form-section">
          <div className="form-section-title">Job Cost Calculator</div>
          <div style={{ fontSize: 12, color: '#888', marginBottom: 12 }}>
            Estimate the job cost, then click “Save cost to job” to store it on this record. Enter the sale price in the Profit &amp; Loss box at the top.
          </div>
          <JobCalculator
            embedded
            savedValues={{ sell: job.calc_sell_price, cost: job.calc_job_cost, profit: job.calc_profit }}
            onSaveResults={({ sell, cost, profit }) => {
              setJobField('calc_job_cost', cost ? String(Math.round(cost * 100) / 100) : '');
              toast.success('Job cost saved — remember to Save the record');
            }}
          />
        </div>
      )}

      {activeTab==='attachments' && (
        <div className="form-section">
          <div className="form-section-title">Attachments</div>
          <Attachments jobId={autoSavedId} />
        </div>
      )}

      {activeTab==='contracts' && (
        <div className="form-section">
          <div className="form-section-title">Contracts — fill &amp; sign</div>
          {!autoSavedId ? (
            <div style={{ color:'var(--gray-500)', fontSize:13 }}>Save the job first to enable contracts.</div>
          ) : (
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(220px,1fr))', gap:12 }}>
              {[
                {type:'coating', name:'Exterior Coating Addendum', icon:'🎨'},
                {type:'hia', name:'Home Improvement Agreement', icon:'📄'},
                {type:'change-order', name:'Change Order Addendum', icon:'🔁'},
                {type:'lien', name:'Mechanics Lien', icon:'⚖️'},
                {type:'cancel', name:'Three-Day Right to Cancel', icon:'🗓️'},
              ].map(c => (
                <div key={c.type}
                  style={{ border:'1px solid var(--gray-200)', borderRadius:12, padding:'16px', display:'flex', flexDirection:'column', gap:8, background:'#fff' }}>
                  <div style={{ fontSize:26 }}>{c.icon}</div>
                  <div style={{ fontWeight:700, fontSize:14, color:'#0d1f3b' }}>{c.name}</div>
                  <a href={`/contract/${c.type}/${autoSavedId}?po=${encodeURIComponent(job.po_number || '')}`}
                    style={{ fontSize:13, color:'var(--green-600)', fontWeight:600, textDecoration:'none' }}>
                    ✍️ Fill &amp; sign in person →
                  </a>
                  <button type="button" onClick={() => makeLink(c.type)}
                    className="btn btn-secondary btn-sm" style={{ marginTop:2 }}>
                    🔗 Send link to customer
                  </button>
                  {contractLinks[c.type] && (
                    <div style={{ marginTop:4 }}>
                      <input readOnly value={contractLinks[c.type]} onFocus={e=>e.target.select()}
                        style={{ width:'100%', fontSize:11, padding:'6px 8px', border:'1px solid var(--gray-300)', borderRadius:6, boxSizing:'border-box' }} />
                      <div style={{ fontSize:10, color:'var(--gray-500)', marginTop:3 }}>Copied — paste via text/email/WhatsApp. Expires in 14 days or once signed.</div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          <div style={{ fontSize:12, color:'var(--gray-500)', marginTop:12 }}>
            Each opens a sign-on-screen form; the signed PDF saves into this job's Attachments.
          </div>
        </div>
      )}

      <div style={{display:'flex',gap:10,justifyContent:'flex-end',marginTop:24,paddingTop:16,borderTop:'1px solid var(--gray-200)'}}>
        <button type="submit" className="btn btn-primary" disabled={loading}>
          {loading ? 'Saving…' : submitLabel}
        </button>
      </div>
    </form>
  );
}
